import { create } from "zustand"
import { persist } from "zustand/middleware"

interface SidebarStore {
  // State
  isOpen: boolean
  expandedMenus: string[]
  isCollapsed: boolean

  // Actions
  toggleSidebar: () => void
  setOpen: (open: boolean) => void
  toggleMenu: (menuName: string) => void
  setExpandedMenus: (menus: string[]) => void
  collapseAllMenus: () => void
  toggleCollapsed: () => void
  setCollapsed: (collapsed: boolean) => void
}

export const useSidebarStore = create<SidebarStore>()(
  persist(
    (set) => ({
      // Initial state
      isOpen: false,
      expandedMenus: [],
      isCollapsed: false,

      // Toggle sidebar open/closed
      toggleSidebar: () => set((state) => ({ isOpen: !state.isOpen })),

      // Set sidebar open state explicitly
      setOpen: (open) => set({ isOpen: open }),

      // Toggle individual menu expansion
      toggleMenu: (menuName) =>
        set((state) => ({
          expandedMenus: state.expandedMenus.includes(menuName)
            ? state.expandedMenus.filter((name) => name !== menuName)
            : [...state.expandedMenus, menuName],
        })),

      // Set expanded menus directly
      setExpandedMenus: (menus) => set({ expandedMenus: menus }),

      // Collapse all menus
      collapseAllMenus: () => set({ expandedMenus: [] }),

      // Toggle sidebar collapsed state (desktop)
      toggleCollapsed: () =>
        set((state) => ({
          isCollapsed: !state.isCollapsed,
          // Collapse all menus when collapsing sidebar
          expandedMenus: !state.isCollapsed ? [] : state.expandedMenus,
        })),

      // Set collapsed state explicitly
      setCollapsed: (collapsed) =>
        set({
          isCollapsed: collapsed,
          // Collapse all menus when collapsing sidebar
          expandedMenus: collapsed ? [] : [],
        }),
    }),
    {
      name: "cloudspry-sidebar-storage", // localStorage key
      // Persist both expanded menus and collapsed state
      partialize: (state) => ({
        expandedMenus: state.expandedMenus,
        isCollapsed: state.isCollapsed,
      }),
    }
  )
)
